'use client';

import { learningintro } from '@/static/learning_intro';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { NEXT_PUBLIC_IMAGE_URL } from '@/common';

interface LearningVersion {
  title: string;
  description: string;
  image: string;
}

// Tách phần content thành component riêng để có thể sử dụng key
const LearningContent: React.FC<{
  version: LearningVersion;
}> = ({ version }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.3,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []); // Chỉ chạy một lần khi mount

  return (
    <section
      ref={sectionRef}
      id="learning"
      className="overflow-hidden px-5 py-16"
      style={{
        backgroundImage: `url('${NEXT_PUBLIC_IMAGE_URL}/image/landing-page/bg_leanring.png')`,
        backgroundSize: 'auto 100%',
        backgroundPosition: 'left top',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f4edff',
      }}
    >
      <div className="max-w-8xl mx-auto">
        <div className="flex flex-col-reverse items-center gap-12 lg:grid lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Image with Fade Up Effect */}
          <div
            className={`flex justify-center transition-all duration-1000 ease-out lg:justify-start ${
              isVisible
                ? 'translate-y-0 opacity-100'
                : 'translate-y-16 opacity-0'
            }`}
          >
            <div className="relative w-full">
              <Image
                src={version.image}
                alt={version.title}
                width={600}
                height={400}
                className="h-auto w-full rounded-lg object-contain"
                loading="lazy"
                sizes="(min-width: 1024px) 50vw, 100vw"
              />
            </div>
          </div>

          {/* Right Column - Title and Description */}
          <div className="space-y-6">
            <svg
              width="174"
              height="53"
              viewBox="0 0 174 53"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16 1.33301H158C166.284 1.33301 173 8.04874 173 16.333V36.333C173 44.6173 166.284 51.333 158 51.333H16C7.71573 51.333 1 44.6173 1 36.333V16.333C1 8.04874 7.71573 1.33301 16 1.33301Z"
                fill="white"
              />
              <path
                d="M16 1.33301H158C166.284 1.33301 173 8.04874 173 16.333V36.333C173 44.6173 166.284 51.333 158 51.333H16C7.71573 51.333 1 44.6173 1 36.333V16.333C1 8.04874 7.71573 1.33301 16 1.33301Z"
                stroke="#E1E2E2"
                strokeWidth="2"
              />
              <path
                d="M30.667 13.5C31.3111 13.5002 31.8328 14.0219 31.833 14.666V15.833H34.167C36.0998 15.8332 37.667 17.4001 37.667 19.333V35.666C37.667 37.5989 36.0998 39.1658 34.167 39.166H17.833C15.9002 39.1658 14.333 37.5989 14.333 35.666V19.333C14.333 17.4001 15.9002 15.8332 17.833 15.833H20.167V14.666C20.1672 14.0219 20.6889 13.5002 21.333 13.5C21.9772 13.5 22.4998 14.0218 22.5 14.666V15.833H29.5V14.666C29.5002 14.0218 30.0228 13.5 30.667 13.5ZM16.667 35.666C16.667 36.3102 17.1888 36.8328 17.833 36.833H34.167C34.8112 36.8328 35.333 36.3102 35.333 35.666V25.166H16.667V35.666ZM17.833 18.166C17.1888 18.1662 16.667 18.6888 16.667 19.333V22.833H35.333V19.333C35.333 18.6888 34.8112 18.1662 34.167 18.166H31.833V19.333C31.833 19.9772 31.3112 20.4998 30.667 20.5C30.0227 20.5 29.5 19.9773 29.5 19.333V18.166H22.5V19.333C22.5 19.9773 21.9773 20.5 21.333 20.5C20.6888 20.4998 20.167 19.9772 20.167 19.333V18.166H17.833Z"
                fill="#9447FF"
              />
              <path
                d="M56.4055 23.8408C56.3459 23.2826 56.0945 22.8479 55.6513 22.5368C55.2124 22.2258 54.6413 22.0702 53.9382 22.0702C53.4439 22.0702 53.0199 22.1448 52.6662 22.2939C52.3125 22.4431 52.0419 22.6455 51.8544 22.9012C51.6669 23.1569 51.571 23.4488 51.5668 23.7769C51.5668 24.0496 51.6286 24.2861 51.7521 24.4864C51.88 24.6867 52.0526 24.8572 52.2699 24.9978C52.4872 25.1341 52.728 25.2492 52.9922 25.343C53.2564 25.4367 53.5227 25.5155 53.7912 25.5795L55.0185 25.8863C55.5128 26.0013 55.9879 26.1569 56.4439 26.3529C56.9041 26.5489 57.3153 26.7961 57.6776 27.0944C58.044 27.3927 58.3338 27.7528 58.5469 28.1746C58.7599 28.5965 58.8665 29.0908 58.8665 29.6576C58.8665 30.4246 58.6705 31.1001 58.2784 31.6839C57.8864 32.2634 57.3196 32.7172 56.5781 33.0454C55.8409 33.3692 54.9482 33.5312 53.8999 33.5312C52.8814 33.5312 51.9972 33.3735 51.2472 33.0581C50.5014 32.7428 49.9176 32.2826 49.4957 31.6775C49.0781 31.0724 48.8523 30.3351 48.8182 29.4658H51.1513C51.1854 29.9218 51.326 30.301 51.5732 30.6036C51.8203 30.9062 52.142 31.132 52.5384 31.2812C52.9389 31.4303 53.3864 31.5049 53.8807 31.5049C54.3963 31.5049 54.848 31.4282 55.2358 31.2748C55.6278 31.1171 55.9347 30.8998 56.1562 30.6228C56.3778 30.3415 56.4908 30.0134 56.495 29.6384C56.4908 29.2975 56.3906 29.0162 56.1946 28.7947C55.9986 28.5688 55.7237 28.3813 55.37 28.2322C55.0206 28.0787 54.6115 27.9424 54.1428 27.8231L52.6534 27.4395C51.5753 27.1626 50.723 26.7428 50.0966 26.1803C49.4744 25.6135 49.1634 24.8614 49.1634 23.9239C49.1634 23.1526 49.3722 22.4772 49.7898 21.8976C50.2116 21.3181 50.7848 20.8685 51.5092 20.5489C52.2337 20.2251 53.054 20.0631 53.9702 20.0631C54.8991 20.0631 55.7131 20.2251 56.4119 20.5489C57.1151 20.8685 57.6669 21.3138 58.0675 21.8849C58.468 22.4516 58.6747 23.1036 58.6875 23.8408H56.4055ZM62.4508 33.333H59.9196L64.5282 20.2421H67.4558L72.0708 33.333H69.5396L66.0431 22.9268H65.9409L62.4508 33.333ZM62.5339 28.2002H69.4373V30.105H62.5339V28.2002ZM67.3216 17.1675H68.9771C68.9728 17.7599 68.698 18.2435 68.1525 18.6185C67.6113 18.9935 66.8784 19.181 65.9537 19.181C65.0289 19.181 64.296 18.9935 63.7548 18.6185C63.2179 18.2435 62.9494 17.7599 62.9494 17.1675H64.5858C64.5858 17.4275 64.688 17.6597 64.8926 17.8643C65.1014 18.0645 65.4551 18.1647 65.9537 18.1647C66.4437 18.1647 66.7931 18.0645 67.002 17.8643C67.2108 17.6597 67.3173 17.4275 67.3216 17.1675ZM65.2889 17.1036L66.3564 15.4417H68.0886L66.5545 17.1036H65.2889ZM73.7951 33.333V20.2421H78.7042C79.7099 20.2421 80.5536 20.4296 81.2354 20.8046C81.9215 21.1796 82.4393 21.6952 82.7887 22.3515C83.1424 23.0035 83.3192 23.7449 83.3192 24.5759C83.3192 25.4154 83.1424 26.1611 82.7887 26.8131C82.435 27.4651 81.913 27.9786 81.2227 28.3536C80.5323 28.7243 79.6822 28.9097 78.6722 28.9097H75.4187V26.9601H78.3526C78.9407 26.9601 79.4222 26.8579 79.7972 26.6533C80.1722 26.4488 80.4492 26.1675 80.6282 25.8096C80.8114 25.4516 80.9031 25.0404 80.9031 24.5759C80.9031 24.1114 80.8114 23.7023 80.6282 23.3486C80.4492 22.9949 80.1701 22.7201 79.7908 22.5241C79.4158 22.3238 78.9322 22.2236 78.3398 22.2236H76.1665V33.333H73.7951ZM89.8439 33.333V20.2421H94.753C95.7587 20.2421 96.6025 20.4168 97.2843 20.7662C97.9703 21.1157 98.4881 21.6057 98.8375 22.2364C99.1912 22.8628 99.3681 23.5937 99.3681 24.4289C99.3681 25.2684 99.1891 25.9971 98.8311 26.615C98.4775 27.2286 97.9554 27.7037 97.2651 28.0404C96.5748 28.3728 95.7267 28.539 94.7211 28.539H91.2246V26.5702H94.4015C94.9895 26.5702 95.4711 26.4893 95.8461 26.3273C96.2211 26.1611 96.498 25.9204 96.677 25.605C96.8603 25.2854 96.9519 24.8934 96.9519 24.4289C96.9519 23.9644 96.8603 23.5681 96.677 23.24C96.4938 22.9076 96.2147 22.6562 95.8397 22.4857C95.4647 22.311 94.981 22.2236 94.3887 22.2236H92.2154V33.333H89.8439ZM96.6067 27.4012L99.8475 33.333H97.2012L94.0179 27.4012H96.6067ZM103.232 33.333H100.701L105.309 20.2421H108.237L112.852 33.333H110.321L106.824 22.9268H106.722L103.232 33.333ZM103.315 28.2002H110.219V30.105H103.315V28.2002ZM119.041 20.2421H121.943L125.83 29.7279H125.983L129.869 20.2421H132.771V33.333H130.496V24.3394H130.374L126.756 33.2947H125.056L121.438 24.3202H121.317V33.333H119.041V20.2421ZM137.035 33.333H134.504L139.112 20.2421H142.04L146.655 33.333H144.124L140.627 22.9268H140.525L137.035 33.333ZM137.118 28.2002H144.021V30.105H137.118V28.2002ZM141.906 17.1675H143.561C143.557 17.7599 143.282 18.2435 142.737 18.6185C142.195 18.9935 141.462 19.181 140.538 19.181C139.613 19.181 138.88 18.9935 138.339 18.6185C137.802 18.2435 137.533 17.7599 137.533 17.1675H139.17C139.17 17.4275 139.272 17.6597 139.477 17.8643C139.685 18.0645 140.039 18.1647 140.538 18.1647C141.028 18.1647 141.377 18.0645 141.586 17.8643C141.795 17.6597 141.901 17.4275 141.906 17.1675ZM139.873 17.1036L140.94 15.4417H142.673L141.138 17.1036H139.873ZM146.222 22.23V20.2421H156.666V22.23H152.62V33.333H150.268V22.23H146.222Z"
                fill="#1C1C1C"
              />
            </svg>
            <h2 className="text-3xl font-bold text-gray-800 lg:text-5xl lg:leading-tight">
              {version.title}
            </h2>
            <p className="text-sm leading-[1.5] text-gray-600 md:text-base">
              {version.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Learning: React.FC = () => {
  const searchParams = useSearchParams();
  const urlVersion = searchParams?.get('version')?.toUpperCase() || 'A';
  const variant = urlVersion === 'A' || urlVersion === 'B' ? urlVersion : 'A';

  // Lấy version tương ứng từ dữ liệu
  const versionKey = `version${variant}` as 'versionA' | 'versionB';
  const version = learningintro[0][versionKey];

  if (!version) {
    console.error(`Version ${variant} not found in learningintro data`);

    return null;
  }

  // Sử dụng key để force re-render khi variant thay đổi
  return <LearningContent key={variant} version={version} />;
};

export default Learning;
